import { useData } from 'vike-react/useData';
import type { Data } from './+data';
import { PillarPageRenderer } from '../../components/layout/PillarPageRenderer';
import { DecretTertiaireDiagnostic } from '../../components/tools/DecretTertiaireDiagnostic';
import { Container } from '../../components/layout/Container';
import { Reveal } from '../../components/motion/Reveal';
import { Badge } from '../../components/ui/badge';
import { useStoredAudienceId } from '../../hooks/useActiveAudience';
import { Calculator, Info, Tractor } from 'lucide-react';

function AgriDisclaimer() {
  // Hook réactif (audience stockée + état d'hydratation) : retourne null côté SSR
  // pour éviter le mismatch React, puis se met à jour après hydratation.
  const audience = useStoredAudienceId();
  if (audience !== 'agriculteurs') return null;
  return (
    <div className="border-b border-amber-200 bg-amber-50 py-3">
      <Container>
        <div className="flex items-start gap-3 text-sm text-amber-900">
          <Tractor className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p>
            <span className="font-semibold">Exploitations agricoles :</span> le Décret Tertiaire
            (dispositif OPERAT) ne s&apos;applique <strong>pas</strong> aux bâtiments à usage
            agricole (art. R.174-22 du Code de la construction). Consultez plutôt notre outil{' '}
            <a
              href="/agriculteurs/diagnostic-cee"
              className="underline underline-offset-2 hover:text-amber-700"
            >
              Diagnostic CEE agricole
            </a>
            .
          </p>
        </div>
      </Container>
    </div>
  );
}

export default function DecretTertiairePage() {
  const { page } = useData<Data>();
  return (
    <>
      <AgriDisclaimer />
      <PillarPageRenderer
        page={page}
        pillarLabel="Décret Tertiaire"
        pillarDescription="Audit, plan d'actions, déclaration OPERAT — atteignez -40 % à 2030 sans sanctions."
        pillarUrl="https://solutionecofrance.fr/decret-tertiaire"
      />

      {/* ── Diagnostic interactif ─────────────────────────────────────── */}
      <section
        id="diagnostic"
        className="relative overflow-hidden border-t border-stone-200/80 bg-gradient-to-br from-stone-50 via-white to-lavender-50/30 py-16 md:py-24"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -right-32 -top-32 h-[500px] w-[500px] rounded-full bg-lavender-200/20 blur-[120px]" />
        </div>

        <Container className="relative">
          <div className="grid items-start gap-10 lg:grid-cols-[1fr,560px]">
            {/* Left — pitch */}
            <div>
              <Reveal>
                <Badge variant="ivory" size="lg" className="gap-2">
                  <Calculator className="h-3.5 w-3.5 text-lavender-700" />
                  Diagnostic interactif
                </Badge>
                <h2 className="mt-5 text-balance font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
                  Calculez votre trajectoire{' '}
                  <span className="bg-gradient-to-r from-lavender-700 to-lavender-500 bg-clip-text text-transparent">
                    OPERAT 2030
                  </span>{' '}
                  en 60 sec.
                </h2>
                <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-stone-600">
                  Type de bâtiment, surface, consommation de référence : on calcule votre objectif
                  chiffré 2030/2040/2050 (méthode Crelat), l’économie potentielle annuelle et un
                  plan d’actions priorisé.
                </p>

                <ul className="mt-6 space-y-2.5 text-[13px] text-stone-700">
                  {[
                    {
                      label: 'Méthode Crelat (valeur relative — arrêté 10 avr. 2020)',
                    },
                    {
                      label: '5 typologies tertiaires couvertes (bureau, commerce, santé…)',
                    },
                    {
                      label: 'Plan d’actions et CTA audit — sans inscription',
                    },
                  ].map((item) => (
                    <li key={item.label} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lavender-500" />
                      {item.label}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 flex items-start gap-2 rounded-xl border border-stone-200 bg-white/80 p-3 backdrop-blur-sm">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lavender-600" />
                  <p className="text-[11px] leading-relaxed text-stone-500">
                    Estimation indicative. L’audit complet vérifie aussi la cible{' '}
                    <strong>Cabs</strong> (valeur absolue, par sous-catégorie d’activité) et intègre
                    les variations climatiques selon les DJU réels du site.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Right — Funnel */}
            <Reveal delay={0.1}>
              <DecretTertiaireDiagnostic />
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}
